import _ from 'lodash'
import { Maybe } from '@/types/general'

const PREFIX = 'volt:global:'

/**
 * Get the current timestamp
 *
 * @returns {number}
 */
const timestamp = (): number => +new Date() / 1000

/**
 * Keys of all stored data
 */
export const API_TOKEN = 'api_token'
export const AUTOLOCK_TIMEOUT = 'autolock_timeout'
export const DARKNESS = 'darkness'
export const DATA_LAST_UPDATED = 'data_last_updated'
export const DRAG_DROP_VAULTS = 'drag_drop_vaults'
export const EMAIL = 'email'
export const EMAIL_VERIFIED = 'email_verfied'
export const ENCRYPTED_PRIVATE_KEY = 'encrypted_private_key'
export const KEYBIND_GLOBAL_SEARCH = 'keybind_global_search'
export const PRIVATE_KEY = 'private_key'
export const PUBLIC_KEY = 'public_key'
export const SECRET_KEY = 'secret_key'
export const SEND_CRASH_REPORTS = 'send_crash_reports'
export const SEND_CRASH_FEEDBACK = 'send_crash_feedback'
export const SHOW_TOOLTIPS = 'show_tooltips'
export const SMART_SEARCH = 'smart_search'
export const STRENGTH_INDICATOR = 'strength_indicator'
export const THEME = 'theme'
export const USER_ID = 'user_id'
export const USER_KEY_HASH = 'user_key_hash'
export const USERNAME = 'username'

export const storeKeys = [
    API_TOKEN,
    AUTOLOCK_TIMEOUT,
    DARKNESS,
    DATA_LAST_UPDATED,
    DRAG_DROP_VAULTS,
    EMAIL,
    EMAIL_VERIFIED,
    ENCRYPTED_PRIVATE_KEY,
    KEYBIND_GLOBAL_SEARCH,
    PRIVATE_KEY,
    PUBLIC_KEY,
    SEND_CRASH_REPORTS,
    SEND_CRASH_FEEDBACK,
    SECRET_KEY,
    SHOW_TOOLTIPS,
    SMART_SEARCH,
    STRENGTH_INDICATOR,
    THEME,
    USER_ID,
    USER_KEY_HASH,
    USERNAME
]

/**
 * Migrate from the 'pass' prefix to the new volt + team prefix
 */
if (localStorage && localStorage.getItem('pass:secret_key')) {
    (() => {
        const oldPrefix = 'pass:'
        Object.keys(localStorage).forEach((key) => {
            if (key.startsWith(oldPrefix)) {
                const value = localStorage.getItem(key)
                localStorage.removeItem(key)
                localStorage.setItem(PREFIX + key.substr(oldPrefix.length), value!)
            }
        })
    })()
}

/**
 * Single storage entry
 */
class StorageEntry {
    public key: string
    public value?: any
    private expiry?: number
    private expires?: number

    /**
     * Create a new storage entry
     * If only the key is specified, the value
     * will be loaded from local storage
     *
     * @param key
     * @param value value to store
     * @param expiry max length between touches (optional)
     */
    constructor(key: string, value?: any, expiry?: number) {
        if (!storeKeys.includes(key)) {
            throw new Error(`PStorage key ${key} not declared`)
        }
        this.key = key
        if (value !== undefined) {
            this.value = value
            this.expiry = expiry
        } else {
            this.load()
        }
    }

    /**
     * Remove from the local storage
     */
    public remove(): void {
        this.value = undefined
        localStorage.removeItem(PREFIX + this.key)
    }

    /**
     * Save data to local storage
     *
     * @returns this for chaining
     */
    public save(): StorageEntry {
        if (this.value === undefined) {
            localStorage.removeItem(PREFIX + this.key)
            return this
        }
        const data = {
            value: this.value,
            expiry: this.expiry,
            expires: (this.expiry ? this.expiry + timestamp() : undefined)
        }
        localStorage.setItem(PREFIX + this.key, JSON.stringify(data))
        return this
    }

    /**
     * Load data from local storage
     *
     * @returns this for chaining
     */
    public load(): StorageEntry {
        const data = JSON.parse(localStorage.getItem(PREFIX + this.key) || '{}')
        if (data !== undefined) {
            this.value = data.value
            this.expiry = data.expiry
            this.expires = data.expires
        }
        return this
    }

    /**
     * Expire the entry if it has passed its expiry time
     *
     * @returns this/undefined for chaining
     */
    public expire(): Maybe<StorageEntry> {
        if (this.expires && this.expires < timestamp()) {
            this.remove()
            return undefined
        }
        return this
    }

    /**
     * Attempt to re-save the entry if it has an expiry
     * Will delete the entry if it is past it's expiry,
     * and will do nothing if there is no expiry
     *
     * @returns this/undefined for chaining
     */
    public touch(): Maybe<StorageEntry> {
        this.expire()
        // save to update expiry
        if (this.expires) {
            this.save()
        }
        return this
    }
}

/**
 * Local Storage wrapper
 */
class PStorage {
    /**
     * Get a StorageEntry from the local storage
     *
     * @param key
     * @param touch whether to touch the entry
     * @returns storage entry or undefined if not found/expired
     */
    private getEntry(key: string, touch: boolean = true): Maybe<StorageEntry> {
        const entry = new StorageEntry(key)
        return touch ? entry.touch() : entry
    }

    /**
     * Get a value from local storage
     * Will delete the entry if it has expired,
     * returning undefined
     *
     * @param key
     * @param touch whether to touch the entry
     * @returns value inside entry, or undefined
     */
    public get(key: string, touch: boolean = true): any {
        const entry = this.getEntry(key, touch)
        if (entry !== undefined && entry.expire() !== undefined) {
            return entry.value
        }
        return undefined
    }

    /**
     * Get a value from local storage, or the
     * default value is the stored one is expired
     * or non existent.
     *
     * If the default value is used it will be stored.
     *
     * @param key
     * @param def Default value
     */
    public getOrDefault(key: string, def: any): any {
        const entry = this.get(key)
        if (entry === undefined) {
            this.set(key, def)
            return def
        }
        return entry
    }

    /**
     * Set a value in local storage
     *
     * @param key
     * @param value value to store
     * @param expiry max length between touches (optional)
     */
    public set(key: string, value: any, expiry?: number): void {
        const entry = new StorageEntry(key, value, expiry)
        entry.save()
    }

    /**
     * Remove an entry from local storage
     *
     * @param key
     */
    public remove(key: string): void {
        localStorage.removeItem(PREFIX + key)
    }

    /**
     * Touch an entry in local storage
     * Will delete the entry if it has expired
     *
     * @param key
     */
    public touch(key: string): void {
        this.getEntry(key, true)
    }
}

class Stored {
    private provider: PStorage

    constructor(provider: PStorage) {
        this.provider = provider
    }

    get apiToken(): string {
        return this.provider.get(API_TOKEN)
    }

    set apiToken(value: string) {
        this.provider.set(API_TOKEN, value)
    }

    get autolockTimeout(): number {
        return this.provider.get(AUTOLOCK_TIMEOUT)
    }

    set dataLastUpdated(value: string | null) {
        this.provider.set(DATA_LAST_UPDATED, value)
    }

    get email(): string {
        return this.provider.get(EMAIL)
    }

    set email(value: string) {
        this.provider.set(EMAIL, value)
    }

    get emailVerified(): boolean {
        return this.provider.get(EMAIL_VERIFIED)
    }

    set emailVerified(value: boolean) {
        this.provider.set(EMAIL_VERIFIED, value)
    }

    get encryptedPrivateKey(): string {
        return this.provider.get(ENCRYPTED_PRIVATE_KEY)
    }

    set encryptedPrivateKey(value: string) {
        this.provider.set(ENCRYPTED_PRIVATE_KEY, value)
    }

    set privateKey(value: string) {
        this.provider.set(PRIVATE_KEY, value, this.provider.getOrDefault(AUTOLOCK_TIMEOUT, 10) * 60)
    }

    get publicKey(): string {
        return this.provider.get(PUBLIC_KEY)
    }

    set publicKey(value: string) {
        this.provider.set(PUBLIC_KEY, value)
    }

    get secretKey(): string {
        return this.provider.get(SECRET_KEY)
    }

    set secretKey(value: string) {
        this.provider.set(SECRET_KEY, value)
    }

    set sendCrashFeedback(value: boolean) {
        this.provider.set(SEND_CRASH_FEEDBACK, value)
    }

    set sendCrashReports(value: boolean) {
        this.provider.set(SEND_CRASH_REPORTS, value)
    }

    set userId(value: string) {
        this.provider.set(USER_ID, value)
    }

    get userKeyHash(): string {
        return this.provider.get(USER_KEY_HASH)
    }

    set userKeyHash(value: string) {
        this.provider.set(USER_KEY_HASH, value)
    }

    get username(): string {
        return this.provider.get(USERNAME)
    }

    set username(value: string) {
        this.provider.set(USERNAME, value)
    }
}

const storage = new PStorage()

/**
 * Facade to get and set localStorage values in the application
 */
export const stored = new Stored(storage)

export default storage
